import { PlusIcon } from "@xola/icons";
import { Alert, Button, ButtonGroup, flash } from "@xola/ui-kit";
import clsx from "clsx";
import { formatDate, now } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { BaseScheduleRepeat, Schedule } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useVisibility } from "../../../hooks/useVisibility";
import { isActive } from "../../../models/Schedule";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { ScheduleTable } from "../../Common/ScheduleTable";
import { SecondaryButton } from "../../Common/SecondaryButton";

enum ButtonType {
    ACTIVE_BLACKOUT = 0,
    EXPIRED_BLACKOUT = 1,
    ALL = 2,
}

interface filteredSchedulesTypes {
    active: Schedule[];
    expired: Schedule[];
}

const filterSchedules = (schedules: Schedule[]) => {
    const filteredSchedules: filteredSchedulesTypes = {
        active: [],
        expired: [],
    };

    filteredSchedules.active = schedules.filter((schedule) => isActive(schedule));
    filteredSchedules.expired = schedules.filter((schedule) => !isActive(schedule));

    return filteredSchedules;
};

// need to make days null for blackoutSchedules api.
const sanatizeDates = (values: Partial<Schedule>): any => {
    return {
        ...values,
        start: formatDate(values.start ?? now()),
        end: values.end ? formatDate(values.end) : null,
        days: values.repeat === BaseScheduleRepeat.CUSTOM ? null : values.days,
    };
};

export const BlackoutSchedules = ({ schedules = [] }: { schedules: Schedule[] }) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [isCreateScheduleModalOpen, { show: showCreateScheduleModal, toggle: toggleCreateScheduleModal }] =
        useVisibility();
    const [deleteScheduleId, setDeleteScheduleId] = useState<ID | null>(null);
    const [activeButton, setActiveButton] = useState(ButtonType.ACTIVE_BLACKOUT);
    const [isAllDelete, setIsAllDelete] = useState(false);
    const filteredSchedules = filterSchedules(schedules);

    const handleButtonChange = (type: ButtonType) => {
        setActiveButton(type);
    };

    const { mutateAsync: createEditSchedule } = useMutation(async (payload: Partial<Schedule>) => {
        payload = sanatizeDates(payload);
        return payload.id
            ? api.put(`/users/${seller.id}/blackoutSchedules/${payload.id}`, payload)
            : api.post(`/users/${seller.id}/blackoutSchedules`, payload);
    });

    const handleScheduleCreate = async (values: Schedule) => {
        await createEditSchedule(values);
        invalidate("sellers", seller.id);
        flash.success("Successfully created schedule");
    };

    const handleScheduleEdit = async (values: Schedule) => {
        await createEditSchedule(values);
        invalidate("sellers", seller.id);
        flash.success("Successfully updated schedule");
    };

    const { mutateAsync: deleteSchedule, isLoading } = useMutation(async (id: ID) => {
        return api.delete(`/users/${seller.id}/blackoutSchedules/${id}`);
    });

    const handleDelete = async () => {
        if (deleteScheduleId) {
            if (isAllDelete) {
                for (const schedule of getVisibleSchedules(activeButton)) {
                    if (schedule.id) {
                        await deleteSchedule(schedule.id);
                    }
                }
            } else {
                await deleteSchedule(deleteScheduleId);
            }

            invalidate("sellers", seller.id);

            setIsAllDelete(false);
            flash.success("Successfully deleted schedule ");
            setDeleteScheduleId(null);
        }
    };

    const getVisibleSchedules = (type: ButtonType) => {
        switch (type) {
            case ButtonType.ACTIVE_BLACKOUT:
                return filteredSchedules.active;
            case ButtonType.EXPIRED_BLACKOUT:
                return filteredSchedules.expired;
            default:
                return schedules;
        }
    };

    return (
        <div className="flex max-w-[560px] flex-col space-y-3">
            {schedules.length > 0 ? (
                <div className="flex items-center justify-between">
                    <ButtonGroup size="small" value={activeButton} onChange={handleButtonChange}>
                        <ButtonGroup.Button
                            type="button"
                            className={clsx(filteredSchedules.active.length > 0 ? "visible" : "hidden")}
                        >
                            Active Blackout <NoTranslate>({filteredSchedules.active.length})</NoTranslate>
                        </ButtonGroup.Button>
                        <ButtonGroup.Button
                            type="button"
                            className={clsx(filteredSchedules.expired.length > 0 ? "visible" : "hidden")}
                        >
                            Expired Blackout <NoTranslate>({filteredSchedules.expired.length})</NoTranslate>
                        </ButtonGroup.Button>
                        <ButtonGroup.Button type="button" className={clsx(schedules.length > 0 ? "visible" : "hidden")}>
                            All <NoTranslate>({schedules.length})</NoTranslate>
                        </ButtonGroup.Button>
                    </ButtonGroup>

                    <SecondaryButton type="button" size="small" icon={<PlusIcon />} onClick={showCreateScheduleModal}>
                        Add Blackout Schedule
                    </SecondaryButton>
                </div>
            ) : (
                <Alert color="primary">
                    <div className="flex items-center justify-between">
                        <span>You have not created any global blackout schedules.</span>
                        <Button type="button" size="small" icon={<PlusIcon />} onClick={showCreateScheduleModal}>
                            Blackout Schedule
                        </Button>
                    </div>
                </Alert>
            )}

            <ScheduleTable
                blackoutSchedules
                schedules={getVisibleSchedules(activeButton)}
                showCreateModal={isCreateScheduleModalOpen}
                activeButton={activeButton}
                setIsAllDelete={(isAll) => setIsAllDelete(isAll)}
                onCreateModalVisibilityToggle={toggleCreateScheduleModal}
                onCreate={handleScheduleCreate}
                onEdit={handleScheduleEdit}
                onDelete={(scheduleId: ID) => setDeleteScheduleId(scheduleId)}
            />

            <PromptModal
                title="Delete Schedule?"
                isLoading={isLoading}
                isOpen={!!deleteScheduleId}
                onConfirm={handleDelete}
                onClose={() => {
                    setDeleteScheduleId(null);
                    setIsAllDelete(false);
                }}
            >
                <span>
                    Deleting this blackout schedule may result in previously unavailable future trips becoming available
                    for bookings.
                </span>
            </PromptModal>
        </div>
    );
};
